import { TDocumentData } from "../models";
import {
  deleteDocumentDataById,
  patchFormFileMetadata,
  uploadFormFile,
} from "../network";

/**
 * RFF namespace where document accordion form values store file-picker intents
 * alongside the main JSON payload. Stripped from values before they reach
 * `saveDocumentData`, so it never appears in the persisted main payload.
 */
export const FILES_NAMESPACE = "__files";

export type TEmptyFileIntent = {
  mode: "empty";
};

export type TKeepFileIntent = {
  mode: "keep";
  id: string;
  dataId: string;
  url: string;
  mimeType?: string;
  originalName?: string;
  kind?: string;
  persistedKind?: string;
};

export type TDeleteFileIntent = {
  mode: "delete";
  id: string;
  dataId: string;
  url: string;
  kind?: string;
};

export type TUploadFileIntent = {
  mode: "upload";
  file: File;
  kind?: string;
};

export type TActiveFileIntent = TKeepFileIntent | TUploadFileIntent;

export type TFileIntent =
  | TEmptyFileIntent
  | TKeepFileIntent
  | TDeleteFileIntent
  | TUploadFileIntent;

export type TFileIntentMap = Record<string, TFileIntent | TFileIntent[]>;

/**
 * Splits a stored file `dataId` into the configured field name and a flag
 * indicating whether the field accepts multiple files. The cabinet keeps the
 * widget's two separator conventions: `field:uuid` and the legacy
 * `field__index`.
 */
export const parseFileField = (
  dataId: string,
): { fieldName: string; isMultiple: boolean } => {
  const colonIdx = dataId.indexOf(":");
  const underscoreIdx = dataId.indexOf("__");
  const sepIdx = colonIdx !== -1 ? colonIdx : underscoreIdx;
  const isMultiple = sepIdx !== -1;

  return {
    fieldName: isMultiple ? dataId.substring(0, sepIdx) : dataId,
    isMultiple,
  };
};

export const buildKeepIntent = (data: TDocumentData): TKeepFileIntent => {
  const kind = data.fileInfo?.kind || undefined;

  return {
    mode: "keep",
    id: data.id,
    dataId: data.dataId,
    url: data.dataLink || "",
    mimeType: data.fileInfo?.mimeType || undefined,
    originalName: data.fileInfo?.originalName || undefined,
    kind,
    persistedKind: kind,
  };
};

export const normalizeFileIntents = (
  value: TFileIntent | TFileIntent[] | undefined,
): TFileIntent[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const isActiveFileIntent = (
  intent: TFileIntent,
): intent is TActiveFileIntent => {
  return intent.mode === "keep" || intent.mode === "upload";
};

export const getActiveFileIntents = (
  value: TFileIntent | TFileIntent[] | undefined,
): TActiveFileIntent[] => {
  return normalizeFileIntents(value).filter(isActiveFileIntent);
};

/**
 * Mirrors the widget rule: a `kind` is "selected" only when it's a non-empty,
 * non-whitespace string. Empty `kind` on an active intent means the file
 * upload would be rejected by the backend with a kind-required error.
 */
export const hasSelectedFileKind = (intent: TActiveFileIntent): boolean => {
  return typeof intent.kind === "string" && intent.kind.trim().length > 0;
};

/**
 * Inserts a new intent into the per-field map, preserving the multiple/single
 * shape so a multi-file field stores an array and a single-file field stores
 * a scalar.
 */
export const insertFileIntent = (
  map: TFileIntentMap,
  fieldName: string,
  isMultiple: boolean,
  intent: TFileIntent,
): void => {
  if (isMultiple) {
    if (!map[fieldName]) {
      map[fieldName] = [];
    } else if (!Array.isArray(map[fieldName])) {
      map[fieldName] = [map[fieldName] as TFileIntent];
    }
    (map[fieldName] as TFileIntent[]).push(intent);
    return;
  }

  if (Array.isArray(map[fieldName])) {
    (map[fieldName] as TFileIntent[]).push(intent);
  } else {
    map[fieldName] = intent;
  }
};

/**
 * Builds the initial `__files` map for an accordion from the document's file
 * data entries. Field names come from `parseFileField`, so this works for
 * config-driven fields without needing the field list itself.
 */
export const buildInitialFileIntents = (
  fileItems: TDocumentData[],
): TFileIntentMap => {
  const files: TFileIntentMap = {};

  fileItems.forEach((item) => {
    if (item.type !== "file") return;

    const { fieldName, isMultiple } = parseFileField(item.dataId);
    insertFileIntent(files, fieldName, isMultiple, buildKeepIntent(item));
  });

  return files;
};

type FileOps = {
  deletes: { id: string }[];
  uploads: { fieldName: string; file: File; kind?: string }[];
  patches: { dataId: string; kind?: string }[];
};

/**
 * Compares the staged file map against itself to derive the concrete
 * delete/upload/metadata-patch operations the cabinet must run. Mirrors the
 * widget's `useOnBoardingStepUpload` so the same intent shapes work on both
 * sides.
 */
export const planFileOperations = (files: TFileIntentMap | undefined): FileOps => {
  const ops: FileOps = { deletes: [], uploads: [], patches: [] };
  if (!files) return ops;

  for (const fieldName in files) {
    const intents = normalizeFileIntents(files[fieldName]);

    for (const intent of intents) {
      if (intent.mode === "delete") {
        ops.deletes.push({ id: intent.id });
      } else if (intent.mode === "upload") {
        ops.uploads.push({
          fieldName,
          file: intent.file,
          kind: intent.kind,
        });
      } else if (
        intent.mode === "keep" &&
        intent.kind !== intent.persistedKind
      ) {
        ops.patches.push({ dataId: intent.dataId, kind: intent.kind });
      }
    }
  }

  return ops;
};

/**
 * Executes the planned operations against the backend. Operations of each
 * kind run in parallel; failures reject the returned promise so the
 * accordion Save error path can surface them under a single status.
 */
export const processFileIntents = async (
  documentId: string,
  files: TFileIntentMap | undefined,
): Promise<void> => {
  const { deletes, uploads, patches } = planFileOperations(files);

  if (deletes.length > 0) {
    await Promise.all(deletes.map(({ id }) => deleteDocumentDataById(id)));
  }

  if (uploads.length > 0) {
    await Promise.all(
      uploads.map(({ fieldName, file, kind }) =>
        uploadFormFile(documentId, fieldName, file, kind),
      ),
    );
  }

  if (patches.length > 0) {
    await Promise.all(
      patches.map(({ dataId, kind }) =>
        patchFormFileMetadata(documentId, dataId, { kind }),
      ),
    );
  }
};
